import React, {memo, useEffect, useState} from 'react';
import {Player, PlayerEvent} from 'bitmovin-player';
import styled from 'styled-components';
import PlayerUI from './PlayerUI';
import Adapter from 'youbora-adapter-bitmovin8';
import {useSelector} from 'react-redux';
import {selectCanAutoPlayVideo} from '../../store/reducers';
import {
    selectPlayerAudio,
    selectPlayerVolume,
} from '../../store/selectors/player';
import {PlayerKeyboardControl} from '../../utils/player/KeyboardControls';
import ReactionPicker from '../Reactions/ReactionPicker';
import {getCurrentLang} from '../../utils/i18n';

function ab2str(buf) {
    return String.fromCharCode.apply(null, new Uint8Array(buf));
}

function str2ab(str) {
    var buf = new ArrayBuffer(str.length);
    var bufView = new Uint8Array(buf);
    for (var i = 0, strLen = str.length; i < strLen; i++) {
        bufView[i] = str.charCodeAt(i);
    }
    return bufView;
}

var adFinished = false;

export const PV2Event = {
    PLAY: 'play',
    PAUSE: 'pause',
    SEEK_START: 'seek_start',
    SEEK_END: 'seek_end',
    AD_START: 'ad_start',
    AD_END: 'ad_end',
    AD_SKIP: 'ad_skip',
    AD_ERROR: 'ad_error',
    ERROR: 'error',
    READY: 'ready',
    SOURCE_LOADED: 'source_loaded',
    TIME_UPDATE: 'time_update',
    DURATION_CHANGE: 'duration_change',
    ENDED: 'ended',
    BUFFER_START: 'buffer_start',
    BUFFER_END: 'buffer_end',
};

const PlayerV2 = React.forwardRef(
    (
        {
            className,
            hideUI,
            hideFullScreen,
            ispToken,
            content,
            youbora,
            widevineLAUrl,
            fairplayLAUrl,
            fairplayCertificateUrl,
            onPlayerReady,
            onEvent,
            isLive,
            adTag,
            onRestart,
            hasNext,
            onCountDownComplete,
            muted,
            disableKeyboard,
            isAVS,
        },
        player,
    ) => {
        const [dispatchedReady, setDispatchedReady] = useState(false);
        const [isWaitingAd, setIsWaitingAd] = useState(false);
        const canAutoPlay = useSelector(selectCanAutoPlayVideo) && !muted;
        const volume = useSelector(selectPlayerVolume);
        const defaultLang = getCurrentLang() === 'es' ? 'SPA' : 'POR';
        const preferedAudioLanguage =
            useSelector(selectPlayerAudio) || defaultLang;

        const startPlayback = () => {
            adFinished = true;
            setIsWaitingAd(false);
            if (player.current) {
                onEvent({type: PV2Event.PLAY});
            }
        };

        useEffect(() => {
            const playerConfig = {
                key: '59be50ed-f8b4-4ee3-8ec4-c24560a462ff',
                ui: false,
                playback: {
                    autoplay: true,
                },
                ...(adTag && {
                    advertising: {
                        adBreaks: [{tag: {url: adTag, type: 'vast'}}],
                    },
                }),

                events: {
                    [PlayerEvent.Play]: data => {
                        if (!dispatchedReady) {
                            onPlayerReady();
                            setDispatchedReady(true);
                        }
                        if (adFinished) {
                            onEvent({type: PV2Event.PLAY});
                        }
                    },
                    [PlayerEvent.Paused]: data => {
                        onEvent({type: PV2Event.PAUSE});
                    },
                    [PlayerEvent.Error]: data => {
                        onEvent({type: PV2Event.ERROR, payload: data});
                    },
                    [PlayerEvent.AdStarted]: data => {
                        onEvent({type: PV2Event.AD_START});
                        setIsWaitingAd(true);
                    },
                    [PlayerEvent.AdFinished]: data => {
                        onEvent({type: PV2Event.AD_END});
                        startPlayback();
                    },
                    [PlayerEvent.AdSkipped]: data => {
                        onEvent({type: PV2Event.AD_SKIP});
                        startPlayback();
                    },
                    [PlayerEvent.AdError]: data => {
                        onEvent({type: PV2Event.AD_ERROR});
                        startPlayback();
                    },
                    [PlayerEvent.StallStarted]: data => {
                        onEvent({type: PV2Event.BUFFER_START});
                    },
                    [PlayerEvent.StallEnded]: data => {
                        onEvent({type: PV2Event.BUFFER_END});
                    },
                    [PlayerEvent.Seek]: data => {
                        onEvent({type: PV2Event.SEEK_START});
                    },
                    [PlayerEvent.Seeked]: data => {
                        onEvent({type: PV2Event.SEEK_END});
                    },
                    [PlayerEvent.PlaybackFinished]: data => {
                        onEvent({type: PV2Event.ENDED});
                    },
                    [PlayerEvent.TimeChanged]: data => {
                        onEvent({type: PV2Event.TIME_UPDATE, data});
                    },
                },
            };

            const playerSource = {
                ...(content.src.indexOf('m3u8') > -1
                    ? {
                          hls: content.src,
                      }
                    : {
                          dash: content.src,
                      }),
                ...(isLive &&
                    isAVS && {
                        options: {
                            hlsWithCredentials: true,
                            manifestWithCredentials: true,
                            withCredentials: true,
                        },
                    }),

                ...(ispToken && {
                    drm: {
                        ...(widevineLAUrl && {
                            widevine: {
                                LA_URL: widevineLAUrl,
                                headers: {
                                    'x-isp-token': ispToken,
                                },
                            },
                        }),
                        ...(fairplayLAUrl &&
                            fairplayCertificateUrl && {
                                fairplay: {
                                    LA_URL: fairplayLAUrl,
                                    certificateURL: fairplayCertificateUrl,
                                    headers: {
                                        'x-isp-token': ispToken,
                                    },
                                    prepareContentId: skd => {
                                        const parts = skd.split('/');
                                        return parts[parts.length - 1];
                                    },
                                    prepareCertificate: certificate => {
                                        const str = ab2str(certificate)
                                            .split('"')
                                            .join('');
                                        return str2ab(atob(str));
                                    },
                                    prepareMessage: (event, session) => {
                                        return event.messageBase64Encoded;
                                    },
                                    prepareLicense: license => {
                                        return license.split('"').join('');
                                    },
                                    useUint16InitData: true,
                                },
                            }),
                    },
                }),
            };

            const setupPlayer = () => {
                const _player = new Player(
                    document.getElementById('player'),
                    playerConfig,
                );

                _player.load(playerSource).then(
                    async () => {
                        if (!canAutoPlay) {
                            //for some reason, mute does not always work, but volume always does
                            _player.setVolume(0);
                        } else {
                            _player.setVolume(volume);
                        }
                        //set default audio language
                        let audios = _player.getAvailableAudio();
                        //find audio with same language, or first one
                        var audio = audios.find(
                            audio => audio.lang === preferedAudioLanguage,
                        );
                        if (!audio && audios.length > 0) {
                            audio = audios[0];
                        }
                        if (audio) {
                            _player.setAudio(audio.id);
                        }
                    },
                    () => {
                        console.log('Error while loading source');
                    },
                );
                if (youbora) {
                    window.plugin.setOptions({
                        'content.isLive': isLive,
                        'content.title': youbora.title,
                        'content.id': youbora.id,
                        'content.type': youbora.type,
                        'content.customDimension.1': youbora.customDimension1,
                    });

                    window.plugin.setAdapter(new Adapter(_player));
                }

                player.current = _player;

                if (!disableKeyboard) {
                    new PlayerKeyboardControl(_player);
                }
            };

            setupPlayer();

            const destroyPlayer = () => {
                if (player.current) {
                    player.current.destroy();
                    player.current = null;
                }
            };

            return () => {
                destroyPlayer();
            };
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);
        return (
            <PlayerWrapper id="player" className={className}>
                {player.current && !hideUI && (
                    <PlayerUI
                        player={player.current}
                        isLive={isLive}
                        isWaitingAd={isWaitingAd}
                        startMuted={!canAutoPlay}
                        onRestart={onRestart}
                        hasNext={hasNext}
                        onComplete={onCountDownComplete}
                        hideFullScreen={hideFullScreen}
                    />
                )}
                {isLive && !hideUI && !isWaitingAd && false && (
                    <ReactionPicker room={String(content.id)} />
                )}
            </PlayerWrapper>
        );
    },
);

const PlayerWrapper = styled.div`
    width: 100%;
    height: 100%;
`;

export default memo(PlayerV2);
